// The iframe side for a stock Chatwoot-based CRM (the Zavis CRM), as a drop-in for HostBridge.
//
// The CRM posts its dashboard-app context to the frame on load and again whenever the frame posts
// "chatwoot-dashboard-app:fetch-info" to its parent: `{ event: "appContext" | "chatwoot-auth",
// data: { currentUser | currentAgent, account, auth: { access-token, client, uid }, conversation?,
// contact? } }`, usually as a JSON string. There is no app token in it: the bridge exchanges the CRM
// credentials for the app's own session token at the app backend (`sessionUrl`), which checks them
// against the CRM. Security rules as in bridge.ts:
// - only messages whose origin is allow-listed AND whose source is our parent window are read;
// - we only ever post to allow-listed origins, never '*';
// - the CRM credentials and the session token live in this object's memory only.
//
// TEMPORARY, opt-in (`handoff`): a CRM that posts `auth` to sidebar apps only (the Zavis CRM today)
// leaves the conversation tab nothing to sign in with. With `handoff`, the sidebar asks the app
// backend for a handoff token after signing in and keeps it in localStorage (handoff-store.ts), and
// a tab without `auth` exchanges it for a session. Remove once the CRM posts `auth` to conversation
// apps too.

import {
  FETCH_INFO_MESSAGE,
  type HostAction,
  HOST_ACTIONS,
  type NavigateTarget,
  type Theme,
} from '../protocol.ts';
import type { Role } from '../types.ts';
import { applyThemeClass, type BridgeWindow, HostError, type HostState } from './bridge.ts';
import { HandoffStore, type HandoffStorage } from './handoff-store.ts';
import { tokenExpiresAt } from './jwt.ts';

export interface ChatwootCredentials {
  'access-token': string;
  client: string;
  uid: string;
}

/** What the CRM's context message says (not trusted by the server: it re-checks the credentials). */
export interface ChatwootContext {
  account: { id: number; name: string };
  currentAgent: { id: number; name: string; email: string; role: Role } | null;
  /** Missing when the CRM does not hand the credentials to this kind of dashboard app. */
  auth: ChatwootCredentials | null;
  conversation: { id: number; inbox_id: number } | null;
  contact: { id: number } | null;
  theme: Theme | null;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const positiveInt = (value: unknown): number | null => {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof number === 'number' && Number.isInteger(number) && number > 0 ? number : null;
};

const nonEmpty = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

function parseAuth(value: unknown): ChatwootCredentials | null {
  if (!isObject(value)) return null;
  const accessToken = nonEmpty(value['access-token']);
  const client = nonEmpty(value.client);
  const uid = nonEmpty(value.uid);
  return accessToken && client && uid ? { 'access-token': accessToken, client, uid } : null;
}

function parseAgent(value: unknown): ChatwootContext['currentAgent'] {
  if (!isObject(value)) return null;
  const id = positiveInt(value.id);
  if (!id) return null;
  return {
    id,
    name: typeof value.name === 'string' ? value.name : '',
    email: typeof value.email === 'string' ? value.email : '',
    role: value.role === 'administrator' ? 'administrator' : 'agent',
  };
}

/**
 * Parses a message from the CRM: `{ event: "appContext" | "chatwoot-auth", data }` (or the bare
 * data), as an object or a JSON string. Returns null for anything else, including contexts without
 * an account (the account may also come from the conversation).
 */
export function parseChatwootContext(raw: unknown): ChatwootContext | null {
  let value = raw;
  if (typeof value === 'string') {
    if (!value.startsWith('{')) return null;
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  if (!isObject(value)) return null;
  let data: unknown = value;
  if ('event' in value) {
    if (value.event !== 'appContext' && value.event !== 'chatwoot-auth') return null;
    data = value.data;
  }
  if (!isObject(data)) return null;

  const conversationRaw = isObject(data.conversation) ? data.conversation : null;
  const conversationId = positiveInt(conversationRaw?.id);
  const inboxId = positiveInt(conversationRaw?.inbox_id);
  const conversation = conversationId && inboxId ? { id: conversationId, inbox_id: inboxId } : null;

  const accountRaw = isObject(data.account) ? data.account : null;
  const accountId = positiveInt(accountRaw?.id) ?? positiveInt(conversationRaw?.account_id);
  if (!accountId) return null;

  const sender =
    isObject(conversationRaw?.meta) && isObject(conversationRaw.meta.sender)
      ? conversationRaw.meta.sender
      : null;
  const contactId = positiveInt(isObject(data.contact) ? data.contact.id : null) ?? positiveInt(sender?.id);

  return {
    account: { id: accountId, name: typeof accountRaw?.name === 'string' ? accountRaw.name : '' },
    currentAgent: parseAgent(data.currentUser) ?? parseAgent(data.currentAgent),
    auth: parseAuth(data.auth),
    conversation,
    contact: contactId ? { id: contactId } : null,
    theme: data.theme === 'dark' || data.theme === 'light' ? data.theme : null,
  };
}

/** What the app backend answers to a sign-in (`POST /api/host/session`). */
interface SessionAnswer {
  token: string;
  user?: { id?: number; name?: string; email?: string; role?: string };
}

type ErrorAnswer = { error?: { code?: string; message?: string } };

/** TEMPORARY: the sidebar-to-conversation-tab session handoff (see the header). */
export interface ChatwootHandoffOptions {
  /** Issues a handoff token for the signed-in user, e.g. `/forms/api/host/handoff`. */
  issueUrl: string;
  /** Exchanges a handoff token for a session, e.g. `/forms/api/host/session-from-handoff`. */
  sessionUrl: string;
  /** Shown in a tab without `auth` when there is no usable handoff. */
  missingMessage?: string;
  /** localStorage key prefix; the key is `<prefix>:<accountId>:<userId>`. Default `waforms:handoff`. */
  storageKeyPrefix?: string;
  /** Testing seam: the storage to use (default `window.localStorage`, null when blocked). */
  storage?: () => HandoffStorage | null;
}

/** What the app backend answers to `POST <issueUrl>`. */
interface HandoffAnswer {
  handoff?: unknown;
  issued_at?: unknown;
  expires_at?: unknown;
  account_id?: unknown;
  user_id?: unknown;
}

/** Answers of `<handoff.sessionUrl>` after which the stored handoff is useless. */
const DEAD_HANDOFF_CODES = new Set([
  'handoff_missing',
  'handoff_expired',
  'handoff_invalid',
  'handoff_mismatch',
]);

const DEFAULT_HANDOFF_MISSING_MESSAGE = 'Open this app from the sidebar once, then reload this tab.';

export interface ChatwootBridgeOptions {
  /** Exact CRM dashboard origins, e.g. ['https://crm.zavis.ai']. */
  allowedOrigins: readonly string[];
  /** The app backend's sign-in endpoint, e.g. `/forms/api/host/session`. */
  sessionUrl: string;
  /** Toggle the `dark` class on <html> when the CRM says which theme it uses. Default true. */
  syncTheme?: boolean;
  /**
   * Host actions the CRM has no counterpart for (toast, open-app, insert-reply), and every action
   * when running standalone. Default: logged to the console.
   */
  onUnsupportedAction?: (action: HostAction) => void;
  /** Opens CRM pages and links. Default: `window.open(url, '_blank', 'noopener,noreferrer')`. */
  openWindow?: (url: string) => void;
  handshakeTimeoutMs?: number;
  /** How long a token refresh waits for the CRM to re-send its context before re-using the last. */
  refreshContextTimeoutMs?: number;
  /** TEMPORARY: sign-in for tabs without `auth` through a handoff from the sidebar. Off when absent. */
  handoff?: ChatwootHandoffOptions;
  /** Testing seams. */
  window?: BridgeWindow;
  fetch?: typeof fetch;
}

const INITIAL_STATE: HostState = {
  status: 'loading',
  error: null,
  account: null,
  currentAgent: null,
  conversation: null,
  contact: null,
  theme: 'light',
  hostOrigin: null,
  hasToken: false,
};

const RETRY_FETCH_INFO_AFTER_MS = [1_500, 4_000];

const NO_AUTH_MESSAGE =
  'The CRM did not send your sign-in to this app. Ask your CRM administrator to enable it for this ' +
  'dashboard app, then reload the page.';

export class ChatwootBridge {
  readonly #options: ChatwootBridgeOptions;
  readonly #allowed: Set<string>;
  #win: BridgeWindow | null = null;
  #state: HostState = INITIAL_STATE;
  #token: string | null = null;
  #context: ChatwootContext | null = null;
  #listeners = new Set<() => void>();
  #timers: ReturnType<typeof setTimeout>[] = [];
  #signingIn: Promise<string> | null = null;
  #refreshing: Promise<string> | null = null;
  #awaitingContext: ((context: ChatwootContext) => void) | null = null;
  #started = false;
  readonly #handoffStore: HandoffStore | null;
  /** `<accountId>:<userId>` pairs a handoff was issued for since this page loaded. */
  readonly #handoffIssued = new Set<string>();
  #handoffRequest: Promise<void> | null = null;

  constructor(options: ChatwootBridgeOptions) {
    this.#options = options;
    this.#allowed = new Set(options.allowedOrigins.map((origin) => origin.replace(/\/+$/, '').toLowerCase()));
    this.#handoffStore = options.handoff
      ? new HandoffStore({ prefix: options.handoff.storageKeyPrefix, storage: options.handoff.storage })
      : null;
  }

  // ---- store (useSyncExternalStore) ------------------------------------------------------------

  getState = (): HostState => this.#state;

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  #setState(patch: Partial<HostState>): void {
    this.#state = { ...this.#state, ...patch, hasToken: this.#token !== null };
    for (const listener of this.#listeners) listener();
  }

  // ---- lifecycle -------------------------------------------------------------------------------

  start(): void {
    if (this.#started) return;
    this.#started = true;
    const win: BridgeWindow | null = this.#options.window ?? (typeof window === 'undefined' ? null : window);
    this.#win = win;
    if (!win) return;
    if (!this.#isFramed(win)) {
      this.#setState({ status: 'standalone' });
      return;
    }
    win.addEventListener('message', this.#onMessage);
    this.#requestContext();
    for (const delay of RETRY_FETCH_INFO_AFTER_MS) {
      this.#timers.push(
        setTimeout(() => {
          if (this.#state.status === 'loading' && !this.#context) this.#requestContext();
        }, delay),
      );
    }
    this.#timers.push(
      setTimeout(() => {
        if (this.#state.status === 'loading' && !this.#context) {
          this.#setState({ status: 'error', error: 'The CRM did not answer. Reload the page.' });
        }
      }, this.#options.handshakeTimeoutMs ?? 10_000),
    );
  }

  stop(): void {
    if (!this.#started) return;
    this.#started = false;
    this.#win?.removeEventListener('message', this.#onMessage);
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers = [];
    this.#awaitingContext = null;
  }

  #isFramed(win: BridgeWindow): boolean {
    return win.parent !== null && (win.parent as unknown) !== win;
  }

  #requestContext(): void {
    const origin = this.#state.hostOrigin;
    // Before the parent's origin is known, address each allowed origin; the browser drops the
    // copies whose origin does not match the parent, so nothing leaks to a foreign embedder.
    const targets = origin ? [origin] : [...this.#allowed];
    for (const target of targets) this.#win?.parent?.postMessage(FETCH_INFO_MESSAGE, target);
  }

  // ---- incoming messages -----------------------------------------------------------------------

  #onMessage = (event: MessageEvent): void => {
    this.handleMessage(event);
  };

  /** Processes one message event. Public for tests; normally fed by the window listener. */
  handleMessage(event: Pick<MessageEvent, 'data' | 'origin' | 'source'>): void {
    const win = this.#win;
    if (!win || !this.#isFramed(win)) return;
    const origin = String(event.origin).toLowerCase();
    if (!this.#allowed.has(origin)) return;
    if (event.source !== (win.parent as unknown)) return;
    const context = parseChatwootContext(event.data);
    if (!context) return;

    const previous = this.#context;
    // Keep the last credentials when a later message comes without them.
    this.#context = context.auth ? context : { ...context, auth: previous?.auth ?? null };
    if (context.theme && this.#options.syncTheme !== false) applyThemeClass(context.theme);
    this.#setState({
      account: context.account,
      currentAgent: context.currentAgent ?? this.#state.currentAgent,
      conversation: context.conversation,
      contact: context.contact,
      theme: context.theme ?? this.#state.theme,
      hostOrigin: origin,
    });

    const waiting = this.#awaitingContext;
    this.#awaitingContext = null;
    if (waiting) {
      waiting(this.#context);
      return;
    }
    const current = this.#context;
    if (!current.auth) {
      if (this.#options.handoff) {
        // Sign in from the stored handoff once per account and user (the context is re-sent often).
        const sameSession =
          this.#token !== null &&
          previous?.account.id === current.account.id &&
          previous.currentAgent?.id === current.currentAgent?.id;
        if (!sameSession) void this.#signIn(current).catch(() => undefined);
        return;
      }
      if (!this.#token) this.#setState({ status: 'error', error: NO_AUTH_MESSAGE });
      return;
    }
    // The CRM re-sends the context often; sign in again only when something that matters changed.
    const sameSession =
      this.#token !== null &&
      previous?.account.id === current.account.id &&
      previous.auth?.uid === current.auth.uid;
    if (!sameSession) void this.#signIn(current).catch(() => undefined);
  }

  // ---- sign-in ---------------------------------------------------------------------------------

  /** Exchanges the CRM credentials for a session token. Concurrent calls share one request. */
  #signIn(context: ChatwootContext): Promise<string> {
    this.#signingIn ??= this.#doSignIn(context).finally(() => {
      this.#signingIn = null;
    });
    return this.#signingIn;
  }

  async #doSignIn(context: ChatwootContext): Promise<string> {
    if (!context.auth) {
      if (this.#options.handoff) return this.#signInWithHandoff(context, this.#options.handoff);
      this.#setState({ status: 'error', error: NO_AUTH_MESSAGE });
      throw new HostError('no_session', NO_AUTH_MESSAGE);
    }
    const answer = await this.#postSession(this.#options.sessionUrl, {
      account_id: context.account.id,
      auth: context.auth,
    });
    if (!answer.ok) return this.#signInFailed(answer.message);
    const token = this.#accept(answer.body);
    if (this.#options.handoff) {
      const userId = answer.body.user?.id;
      if (typeof userId === 'number') void this.#refreshHandoff(token, context.account.id, userId);
    }
    return token;
  }

  /** POSTs a sign-in and reads the answer; never throws. */
  async #postSession(
    url: string,
    payload: unknown,
  ): Promise<{ ok: true; body: SessionAnswer } | { ok: false; code: string | null; message: string }> {
    let response: Response;
    try {
      response = await (this.#options.fetch ?? globalThis.fetch)(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
      });
    } catch {
      return {
        ok: false,
        code: null,
        message: 'The app could not be reached. Check your connection and reload.',
      };
    }
    const body = (await response.json().catch(() => null)) as (SessionAnswer & ErrorAnswer) | null;
    if (!response.ok || !body || typeof body.token !== 'string') {
      return {
        ok: false,
        code: body?.error?.code ?? null,
        message: body?.error?.message ?? `Signing in failed (${response.status}).`,
      };
    }
    return { ok: true, body };
  }

  /** Keeps the session token of a successful sign-in and becomes ready. */
  #accept(body: SessionAnswer): string {
    this.#token = body.token;
    const user = body.user;
    const currentAgent: HostState['currentAgent'] =
      user && typeof user.id === 'number'
        ? {
            id: user.id,
            name: user.name ?? '',
            email: user.email ?? '',
            role: user.role === 'administrator' ? 'administrator' : 'agent',
          }
        : this.#state.currentAgent;
    this.#setState({ status: 'ready', error: null, currentAgent });
    return body.token;
  }

  // ---- TEMPORARY: session handoff from the sidebar ---------------------------------------------

  /** A tab without `auth`: exchanges the handoff stored for the CRM's account and user. */
  async #signInWithHandoff(context: ChatwootContext, handoff: ChatwootHandoffOptions): Promise<string> {
    const missing = handoff.missingMessage ?? DEFAULT_HANDOFF_MISSING_MESSAGE;
    const accountId = context.account.id;
    const userId = context.currentAgent?.id;
    const stored = userId ? this.#handoffStore?.read(accountId, userId) : null;
    if (!userId || !stored) return this.#signInFailed(missing);
    const answer = await this.#postSession(handoff.sessionUrl, {
      handoff: stored.token,
      account_id: accountId,
      user_id: userId,
    });
    if (answer.ok) return this.#accept(answer.body);
    if (answer.code && DEAD_HANDOFF_CODES.has(answer.code)) {
      this.#handoffStore?.remove(accountId, userId);
      return this.#signInFailed(missing);
    }
    return this.#signInFailed(answer.message);
  }

  /**
   * After a sign-in with `auth` (the sidebar): stores a handoff for the conversation tab. A new one
   * on the first sign-in of each user and account since the page loaded, later only when less than
   * half of the stored one's lifetime is left. Failures are ignored: the tab explains what to do.
   */
  #refreshHandoff(sessionToken: string, accountId: number, userId: number): Promise<void> {
    const store = this.#handoffStore;
    const handoff = this.#options.handoff;
    const pair = `${accountId}:${userId}`;
    if (!store || !handoff || this.#handoffRequest) return this.#handoffRequest ?? Promise.resolve();
    if (this.#handoffIssued.has(pair) && !store.needsRefresh(accountId, userId)) return Promise.resolve();
    this.#handoffRequest = this.#issueHandoff(handoff, store, sessionToken, accountId, userId).finally(() => {
      this.#handoffRequest = null;
    });
    return this.#handoffRequest;
  }

  async #issueHandoff(
    handoff: ChatwootHandoffOptions,
    store: HandoffStore,
    sessionToken: string,
    accountId: number,
    userId: number,
  ): Promise<void> {
    let body: HandoffAnswer | null;
    try {
      const response = await (this.#options.fetch ?? globalThis.fetch)(handoff.issueUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', Authorization: `Bearer ${sessionToken}` },
        credentials: 'omit',
      });
      if (!response.ok) return;
      body = (await response.json().catch(() => null)) as HandoffAnswer | null;
    } catch {
      return; // offline: the next sidebar sign-in tries again
    }
    if (
      !body ||
      typeof body.handoff !== 'string' ||
      typeof body.issued_at !== 'number' ||
      typeof body.expires_at !== 'number' ||
      body.account_id !== accountId ||
      body.user_id !== userId
    ) {
      return;
    }
    const written = store.write({
      token: body.handoff,
      accountId,
      userId,
      issuedAt: body.issued_at,
      expiresAt: body.expires_at,
    });
    if (written) this.#handoffIssued.add(`${accountId}:${userId}`);
  }

  #signInFailed(message: string): never {
    // A refresh that fails after a good start keeps the old state; the next API call reports it.
    if (!this.#token) this.#setState({ status: 'error', error: message });
    throw new HostError('refresh_failed', message);
  }

  // ---- tokens ----------------------------------------------------------------------------------

  /** The current session token, if any. Keep it in memory; never persist it. */
  getToken(): string | null {
    return this.#token;
  }

  /** The current token, refreshed first when it expires within `minValiditySeconds`. */
  async getFreshToken(minValiditySeconds = 30): Promise<string> {
    const token = this.#token;
    if (token) {
      const expiresAt = tokenExpiresAt(token);
      if (expiresAt === null || expiresAt - Date.now() / 1000 > minValiditySeconds) return token;
    }
    if (this.#signingIn) return this.#signingIn;
    if (!token && this.#state.status !== 'ready') {
      throw new HostError('no_session', 'No session yet: the CRM has not signed you in');
    }
    return this.refreshToken();
  }

  /**
   * Signs in again: asks the CRM for its context (fresh credentials), falling back to the last
   * credentials it sent if it does not answer (the CRM ignores requests from hidden tabs).
   * Concurrent calls share one request.
   */
  refreshToken(): Promise<string> {
    this.#refreshing ??= this.#doRefresh().finally(() => {
      this.#refreshing = null;
    });
    return this.#refreshing;
  }

  async #doRefresh(): Promise<string> {
    const win = this.#win;
    if (!win || !this.#isFramed(win)) throw new HostError('no_session', 'Not running inside the CRM');
    const fresh = await new Promise<ChatwootContext | null>((resolve) => {
      const timer = setTimeout(() => {
        this.#awaitingContext = null;
        resolve(null);
      }, this.#options.refreshContextTimeoutMs ?? 3_000);
      this.#awaitingContext = (context) => {
        clearTimeout(timer);
        resolve(context);
      };
      this.#requestContext();
    });
    const context = fresh ?? this.#context;
    if (!context) throw new HostError('no_session', 'No session yet: the CRM has not signed you in');
    return this.#signIn(context);
  }

  // ---- host actions ----------------------------------------------------------------------------

  #unsupported(action: HostAction): void {
    if (this.#options.onUnsupportedAction) this.#options.onUnsupportedAction(action);
    else console.info('[crm host action, not supported]', action);
  }

  #open(url: string): void {
    if (this.#options.openWindow) this.#options.openWindow(url);
    else if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Opens a conversation (display id) or contact of the current account in a new CRM tab. */
  navigate(target: NavigateTarget, id: number): void {
    if (target !== 'conversation' && target !== 'contact')
      throw new TypeError('target must be conversation or contact');
    if (!Number.isInteger(id) || id <= 0) throw new RangeError('id must be a positive integer');
    const origin = this.#state.hostOrigin;
    const account = this.#state.account?.id;
    if (!origin || !account) {
      this.#unsupported({ type: HOST_ACTIONS.NAVIGATE, target, id });
      return;
    }
    const section = target === 'conversation' ? 'conversations' : 'contacts';
    this.#open(`${origin}/app/accounts/${account}/${section}/${id}`);
  }

  /** The CRM has no toast for dashboard apps: handed to `onUnsupportedAction`. */
  toast(message: string): void {
    const text = String(message).trim();
    if (text) this.#unsupported({ type: HOST_ACTIONS.TOAST, message: text });
  }

  openApp(path: string): void {
    this.#unsupported({ type: HOST_ACTIONS.OPEN_APP, path });
  }

  insertReply(text: string): void {
    const trimmed = String(text).trim();
    if (trimmed) this.#unsupported({ type: HOST_ACTIONS.INSERT_REPLY, text: trimmed });
  }

  /** Opens an https URL in a new tab. */
  openUrl(url: string): void {
    if (!/^https:\/\//.test(url)) throw new TypeError('openUrl only accepts https:// URLs');
    this.#open(url);
  }

  /** The CRM never asks a dashboard app to change pages. */
  onRoute =
    (_listener: (path: string) => void): (() => void) =>
    () =>
      undefined;
}
